import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Icon from '../../components/common/Icon';
import SdgIcon from '../../components/common/SdgIcon';
import PurposeItemStats from '../../components/common/PurposeItemStats';
import { purposeLabel } from '../../components/common/EventTags';
import { apiRequest } from '../../utils';
import './MemberHome.css';

/* ONE thing the company stands for — a declared value, or one of the 17 UN goals it works
   towards. The screen behind a chip on the home badge.

   It exists because a chip is the shortest possible summary of something a company sat down and
   thought about: a word, at 0.68rem, next to a picture the size of a fingernail. The picture was
   uploaded to be looked at and an SDG carries a reason somebody wrote — neither survives chip
   size, and the badge is where most people meet them.

   ★ Open to an EMPLOYEE, not only the company's admin. Their employer's values are the thing
   they are most likely to tap, and bouncing them to a screen they cannot open would have made
   the chips worse than decoration. The admin gets one extra button: the way into the editor,
   which is the Programmes screen — the values and the goals are written in exactly one place.

   Reached as /purpose/value/<id> and /purpose/sdg/<number>. A goal is addressed by its official
   NUMBER, which is the one thing about an SDG that is the same in the file name, the colour, the
   translated label and the artwork; a value by the row's own id. */

const PurposeDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { kind, itemId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiRequest('GET', `/bravos/company/purpose/${kind}/${itemId}`)
      .then(setData)
      // Not this company's, or not a Bravos BU — either way there is nothing here to read, and
      // the home is where the chip was tapped.
      .catch(() => navigate('/member-home'))
      .finally(() => setLoading(false));
  }, [kind, itemId, navigate]);

  const item = data && data.item;
  const isSdg = kind === 'sdg';
  const name = item && (isSdg ? purposeLabel(t, { ...item, kind: 'sdg' }) : item.name);
  // Each kind has its own editor screen, and it opens with this very item already unfolded —
  // see the ?item= the two screens read. A goal is addressed by its official NUMBER, a value by
  // its id, which is what each of those screens knows how to resolve.
  const editTo = isSdg
    ? `/sdgs?item=${item ? item.number : ''}`
    : `/values?item=${item ? item.id : ''}`;

  return (
    <Base activeNav="home">
      <Card>
        <div className="bravos-brand">
          <div className="bravos-brand__text">
            <Title my="0" className="mt-1">{item ? name : t('loading')}</Title>
            <p className="bravos-brand__name">
              {(data && data.company_name) || t('whatWeStandFor')}
            </p>
          </div>
          <button
            type="button"
            className="bravos-wallet__link bravos-members__back"
            onClick={() => navigate(-1)}
          >
            <Icon name="angle-left" /> {t('back')}
          </button>
        </div>
      </Card>

      {loading || !item ? (
        <Card><span className="auth__spinner" /></Card>
      ) : (
        <Card>
          {/* The picture, at the size it was uploaded for. A value that never got one says so
              rather than leaving a grey rectangle that reads as a failed image. */}
          {isSdg ? (
            <SdgIcon number={item.number} className="purpose__sdg" alt={name} />
          ) : item.image ? (
            <img className="purpose__img" src={item.image} alt="" />
          ) : (
            <p className="purpose__empty">{t('valueNoImage')}</p>
          )}

          {/* The fields under it. A value is its word, the line the company wrote about what
              it means here, and its picture. A goal carries the company's own reason for
              picking it instead. */}
          <p className="bravos-form__label">{isSdg ? t('eventSdgs') : t('eventValues')}</p>
          <p className="purpose__name">{name}</p>

          {!isSdg && (
            <p className={`purpose__reason ${item.description ? '' : 'purpose__reason--empty'}`}>
              {item.description || t('valueNoDesc')}
            </p>
          )}

          {isSdg && (
            <>
              <p className="bravos-form__label">{t('addReason')}</p>
              <p className={`purpose__reason ${item.reason ? '' : 'purpose__reason--empty'}`}>
                {item.reason || t('sdgNoReason')}
              </p>
            </>
          )}

          {data.can_edit && (
            <button
              type="button"
              className="bravos-submit"
              onClick={() => navigate(editTo)}
            >
              {t('edit')}
            </button>
          )}
        </Card>
      )}

      {/* How it lives — cheers naming it, events serving it, what people posted there. Only for
          the engagement admin (`show_stats` is the stats endpoint's own gate), so an employee's
          page stays exactly what it was. */}
      {data && data.show_stats && item && (
        <Card>
          <Title my="1">{t('pisTitle')}</Title>
          <PurposeItemStats kind={kind} itemId={itemId} />
        </Card>
      )}
    </Base>
  );
};

export default PurposeDetail;
